import "./orderPrint.css";

import { DashboardModal } from "@dashboard/components/Modal";
import { OrderDetailsFragment, useOrderPrintShopQuery } from "@dashboard/graphql";
import useLocalStorage from "@dashboard/hooks/useLocalStorage";
import { Box, Button, Checkbox, Option, Select, Text, Textarea } from "@saleor/macaw-ui-next";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MessageDescriptor, useIntl } from "react-intl";

import {
  getPickupPin,
  getRecordedPontoRecolhaAddress,
  isParcelShopDelivery,
} from "../../utils/delivery";
import { orderPrintMessages as messages } from "./messages";
import { PickupTicket } from "./PickupTicket";
import { ShippingLabel } from "./ShippingLabel";
import {
  DEFAULT_SHIPPING_LABEL_FIELDS,
  getPageSizeRule,
  getPaper,
  getPapers,
  OrderPrintDocument,
  OrderPrintPaper,
  pxToMm,
  ShippingLabelFields,
} from "./utils";

const SHIPPING_LABEL_FIELD_LABELS: Record<keyof ShippingLabelFields, MessageDescriptor> = {
  orderNumber: messages.fieldOrderNumber,
  shippingMethod: messages.fieldShippingMethod,
  itemCount: messages.fieldItemCount,
  printedAt: messages.fieldPrintedAt,
};

const LABEL_FIELD_KEYS = Object.keys(SHIPPING_LABEL_FIELD_LABELS) as Array<
  keyof ShippingLabelFields
>;

const PAPER_LABELS: Record<string, MessageDescriptor> = {
  a5: messages.paperA5,
  "a4-half": messages.paperA4Half,
  "label-100x150": messages.paperLabel100x150,
  "roll-80": messages.paperRoll80,
  "roll-58": messages.paperRoll58,
  a4: messages.paperA4,
};

// Widest preview that fits the large modal without scrolling sideways.
const PREVIEW_MAX_WIDTH_PX = 560;
const PX_PER_MM = 96 / 25.4;

interface OrderPrintDialogProps {
  open: boolean;
  onClose: () => void;
  order: OrderDetailsFragment;
  document: OrderPrintDocument;
}

const Notice = ({ children }: { children: ReactNode }) => (
  <Box
    padding={3}
    borderRadius={3}
    borderStyle="solid"
    borderWidth={1}
    borderColor="warning1"
    backgroundColor="warning1"
  >
    <Text size={2}>{children}</Text>
  </Box>
);

export const OrderPrintDialog = ({ open, onClose, order, document }: OrderPrintDialogProps) => {
  const intl = useIntl();
  const { data: shopData } = useOrderPrintShopQuery({ skip: !open });
  const [paperId, setPaperId] = useLocalStorage<string>(`orderPrintPaper:${document}`, "");
  const paper: OrderPrintPaper = getPaper(document, paperId);
  const [storedFields, setStoredFields] = useLocalStorage<Partial<ShippingLabelFields>>(
    "orderPrintShippingLabelFields",
    DEFAULT_SHIPPING_LABEL_FIELDS,
  );
  // Merged with the defaults so a field added later starts out shown.
  const labelFields: ShippingLabelFields = { ...DEFAULT_SHIPPING_LABEL_FIELDS, ...storedFields };
  const allLabelFieldsShown = LABEL_FIELD_KEYS.every(field => labelFields[field]);
  const noLabelFieldShown = LABEL_FIELD_KEYS.every(field => !labelFields[field]);
  const setAllLabelFields = (shown: boolean) =>
    setStoredFields({
      orderNumber: shown,
      shippingMethod: shown,
      itemCount: shown,
      printedAt: shown,
    });
  const recordedPickupPoint = getRecordedPontoRecolhaAddress(order.metadata);
  const [pickupPointAddress, setPickupPointAddress] = useState(recordedPickupPoint);
  const [printedAt, setPrintedAt] = useState(() => new Date());
  const [contentHeightMm, setContentHeightMm] = useState(0);
  const ticketRef = useRef<HTMLDivElement>(null);

  // Start from the order's recorded pickup point (and a fresh timestamp) each time.
  useEffect(() => {
    if (open) {
      setPickupPointAddress(recordedPickupPoint);
      setPrintedAt(new Date());
    }
  }, [open, recordedPickupPoint]);

  // A roll has no fixed page length, so the page is cut to the ticket's height.
  useLayoutEffect(() => {
    if (ticketRef.current) {
      setContentHeightMm(pxToMm(ticketRef.current.offsetHeight));
    }
  }, [open, paper.id, order, shopData, printedAt]);

  const shopName = shopData?.shop?.name ?? "";
  const companyAddress = shopData?.shop?.companyAddress;
  const isPickupTicket = document === "pickupTicket";
  const isPickupPoint = !isPickupTicket && isParcelShopDelivery(order.deliveryType);

  const paperOptions: Option[] = getPapers(document).map(option => ({
    value: option.id,
    label: intl.formatMessage(PAPER_LABELS[option.id]),
  }));
  const pageSizeText = `${paper.pageWidthMm} × ${
    paper.pageHeightMm ?? contentHeightMm + 2 * paper.marginMm
  } mm`;

  const renderDocument = (measure: boolean) =>
    isPickupTicket ? (
      <PickupTicket
        ref={measure ? ticketRef : undefined}
        order={order}
        shopName={shopName}
        companyAddress={companyAddress}
        paper={paper}
        printedAt={printedAt}
      />
    ) : (
      <ShippingLabel
        order={order}
        shopName={shopName}
        companyAddress={companyAddress}
        pickupPointAddress={pickupPointAddress}
        paper={paper}
        printedAt={printedAt}
        fields={labelFields}
      />
    );

  // The preview is drawn at real size (so the roll height can be measured) and
  // scaled down to fit the dialog.
  const sheetWidthMm = paper.pageHeightMm ? paper.pageWidthMm : paper.contentWidthMm;
  const sheetHeightMm = paper.pageHeightMm ?? contentHeightMm;
  const previewScale = Math.min(1, PREVIEW_MAX_WIDTH_PX / (sheetWidthMm * PX_PER_MM));

  const handlePrint = async () => {
    // Stamp the moment of printing, then let React paint it before printing.
    setPrintedAt(new Date());
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    window.print();
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <DashboardModal onChange={onClose} open={open}>
        <DashboardModal.Content size="lg">
          <DashboardModal.Header>
            {intl.formatMessage(
              isPickupTicket ? messages.pickupTicketTitle : messages.shippingLabelTitle,
            )}
          </DashboardModal.Header>

          <Box display="grid" gap={4}>
            <Box display="grid" gap={1}>
              <Select
                label={intl.formatMessage(messages.paper)}
                options={paperOptions}
                value={paperOptions.find(option => option.value === paper.id) ?? null}
                onChange={(option: Option) => setPaperId(option.value)}
                size="small"
                data-test-id="order-print-paper"
              />
              <Text size={2} color="default2">
                {intl.formatMessage(messages.pageSizeHint, { size: pageSizeText })}
              </Text>
            </Box>

            {!isPickupTicket && (
              <Box display="grid" gap={1}>
                <Text size={2} fontWeight="medium">
                  {intl.formatMessage(messages.showOnLabel)}
                </Text>
                <Box display="flex" flexWrap="wrap" gap={4}>
                  <Checkbox
                    checked={
                      allLabelFieldsShown ? true : noLabelFieldShown ? false : "indeterminate"
                    }
                    onCheckedChange={() => setAllLabelFields(!allLabelFieldsShown)}
                    data-test-id="order-print-field-all"
                  >
                    <Text size={2} fontWeight="medium">
                      {intl.formatMessage(messages.fieldAll)}
                    </Text>
                  </Checkbox>
                  {LABEL_FIELD_KEYS.map(field => (
                    <Checkbox
                      key={field}
                      checked={labelFields[field]}
                      onCheckedChange={checked =>
                        setStoredFields({ ...labelFields, [field]: !!checked })
                      }
                      data-test-id={`order-print-field-${field}`}
                    >
                      <Text size={2}>{intl.formatMessage(SHIPPING_LABEL_FIELD_LABELS[field])}</Text>
                    </Checkbox>
                  ))}
                </Box>
              </Box>
            )}

            {shopData && !companyAddress && (
              <Notice>{intl.formatMessage(messages.missingCompanyAddress)}</Notice>
            )}
            {!isPickupTicket && !order.shippingAddress && (
              <Notice>{intl.formatMessage(messages.missingShippingAddress)}</Notice>
            )}
            {isPickupTicket && !getPickupPin(order.privateMetadata) && (
              <Notice>{intl.formatMessage(messages.missingPin)}</Notice>
            )}
            {isPickupPoint && (
              <>
                {!recordedPickupPoint && (
                  <Notice>{intl.formatMessage(messages.pickupPointMissing)}</Notice>
                )}
                <Textarea
                  name="pickupPointAddress"
                  label={intl.formatMessage(messages.pickupPointInput)}
                  helperText={intl.formatMessage(messages.pickupPointInputHelp)}
                  value={pickupPointAddress}
                  onChange={event => setPickupPointAddress(event.target.value)}
                  rows={3}
                  data-test-id="order-print-pickup-point"
                />
              </>
            )}

            <Box
              display="flex"
              justifyContent="center"
              padding={4}
              backgroundColor="default2"
              borderRadius={3}
              overflowY="auto"
              style={{ maxHeight: "60vh" }}
            >
              <div
                style={{
                  width: sheetWidthMm * PX_PER_MM * previewScale,
                  height: sheetHeightMm * PX_PER_MM * previewScale,
                  flexShrink: 0,
                }}
              >
                <div
                  data-test-id="order-print-preview"
                  style={{
                    width: `${sheetWidthMm}mm`,
                    minHeight: `${sheetHeightMm}mm`,
                    padding: paper.pageHeightMm ? `${paper.marginMm}mm` : 0,
                    boxSizing: "border-box",
                    background: "#fff",
                    boxShadow: "0 1px 6px rgba(0, 0, 0, 0.25)",
                    transform: `scale(${previewScale})`,
                    transformOrigin: "top left",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                  }}
                >
                  {renderDocument(true)}
                </div>
              </div>
            </Box>
          </Box>

          <DashboardModal.Actions>
            <Button variant="secondary" onClick={onClose} data-test-id="order-print-close">
              {intl.formatMessage(messages.cancel)}
            </Button>
            <Button variant="primary" onClick={handlePrint} data-test-id="order-print-submit">
              {intl.formatMessage(messages.print)}
            </Button>
          </DashboardModal.Actions>
        </DashboardModal.Content>
      </DashboardModal>

      {createPortal(
        <div className="orderPrintPortalRoot">
          <style>{getPageSizeRule(paper, contentHeightMm)}</style>
          <div
            style={{
              display: "flex",
              justifyContent: paper.contentWidthMm < paper.pageWidthMm ? "center" : "flex-start",
            }}
          >
            {renderDocument(false)}
          </div>
        </div>,
        window.document.body,
      )}
    </>
  );
};
